import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Switch, Space, Card, Typography, message, Popconfirm, Badge } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, NotificationOutlined } from "@ant-design/icons";
import axios from "axios";
import consts from "../../constant";
import Loadingpage from "../Loadingpage/Loadingpage";

const { Title } = Typography;

const Announcements = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${consts.BackendUrl}/announcements`);
      if (response.data.success) {
        setData(response.data.result.docs || []);
      }
    } catch (error) {
      message.error("Failed to fetch announcements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${consts.BackendUrl}/announcements/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("Jetix")}` }
      });
      if (response.data.success) {
        message.success("Announcement deleted");
        fetchAnnouncements();
      }
    } catch (error) {
      message.error("Delete failed");
    }
  };

  const onFinish = async (values) => {
    try {
      const url = editingItem ? `${consts.BackendUrl}/announcements/${editingItem._id}` : `${consts.BackendUrl}/announcements`;
      const method = editingItem ? "patch" : "post";
      
      const response = await axios[method](url, values, {
        headers: { Authorization: `Bearer ${localStorage.getItem("Jetix")}` }
      });

      if (response.data.success) {
        message.success(editingItem ? "Updated successfully" : "Created successfully");
        setIsModalOpen(false);
        fetchAnnouncements();
      }
    } catch (error) {
      message.error("Action failed");
    }
  };

  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Message", dataIndex: "message", key: "message", ellipsis: true },
    { 
      title: "Status", 
      dataIndex: "status", 
      key: "status",
      render: (status) => (
        <Badge 
          status={status ? "success" : "error"} 
          text={status ? "Active" : "Inactive"} 
          style={{ color: "#fff" }}
        />
      )
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            ghost 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)} 
            style={{ borderColor: "#FC4420", color: "#FC4420" }}
          />
          <Popconfirm 
            title="Delete this announcement?" 
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" danger ghost icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      {loading && <Loadingpage />}
      <Card className="card-bg-clr" style={{ borderRadius: "16px", border: "1px solid rgba(255,255,255,0.1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <Space>
            <NotificationOutlined style={{ fontSize: "24px", color: "#FC4420" }} />
            <Title level={3} style={{ color: "#fff", margin: 0 }}>Announcements</Title>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ backgroundColor: "#FC4420", border: "none" }}>
            Add Announcement
          </Button>
        </div>

        <Table 
          columns={columns} 
          dataSource={data} 
          rowKey="_id" 
          pagination={{ pageSize: 10 }}
          className="premium-table"
        />
      </Card>

      <Modal
        title={<span style={{ color: "#fff" }}>{editingItem ? "Edit Announcement" : "Add Announcement"}</span>}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        maskClosable={false}
        centered
        className="custom-modal trade-pattern-modal"
        // styles={{
        //   mask: { backdropFilter: "blur(4px)" },
        //   content: { background: "#111", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.1)" }
        // }}
        maskStyle={{
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="title" label={<span style={{ color: "rgba(255,255,255,0.7)" }}>Title</span>} rules={[{ required: true }]}>
            <Input className="premium-input" />
          </Form.Item>
          <Form.Item name="message" label={<span style={{ color: "rgba(255,255,255,0.7)" }}>Message</span>} rules={[{ required: true }]}>
            <Input.TextArea rows={4} className="premium-input" />
          </Form.Item>
          <Form.Item name="status" label={<span style={{ color: "rgba(255,255,255,0.7)" }}>Status</span>} valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setIsModalOpen(false)} style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.2)" }}>Cancel</Button>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: "#FC4420", border: "none" }}>{editingItem ? "Update" : "Save"}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <style>
        {`
          .premium-table .ant-table {
            background: transparent !important;
            color: #fff !important;
          }
          .premium-table .ant-table-thead > tr > th {
            background: rgba(255,255,255,0.02) !important;
            color: rgba(255,255,255,0.45) !important;
            border-bottom: 1px solid rgba(255,255,255,0.05) !important;
          }
          .premium-table .ant-table-tbody > tr > td {
            border-bottom: 1px solid rgba(255,255,255,0.05) !important;
          }
          .premium-table .ant-table-row:hover > td {
            background: rgba(255,255,255,0.02) !important;
          }
          .premium-input {
            background: rgba(255,255,255,0.05) !important;
            border: 1px solid rgba(255,255,255,0.1) !important;
            color: #fff !important;
            border-radius: 8px !important;
          }
          .premium-input:focus {
            border-color: #FC4420 !important;
            box-shadow: 0 0 0 2px rgba(252, 68, 32, 0.1) !important;
          }
          .ant-modal-header {
            background: #111 !important;
            border-bottom: 1px solid rgba(255,255,255,0.05) !important;
            padding: 16px 24px !important;
          }
          .ant-modal-close {
            color: rgba(255,255,255,0.45) !important;
          }
        `}
      </style>
    </div>
  );
};

export default Announcements;
